import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { toast } from 'react-toastify';
import { getUnit, saveUnit } from '../../services/unidades';
import SettingsMenuButton from '../common/SettingsMenuButton';
import { useAuthSession } from '../../contexts/AuthSessionContext';

const defaultForm = {
  nome: '',
  endereco: '',
  cidade: '',
  estado: '',
  cep: '',
  ativo: true,
};

const UnitForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id && id !== 'novo');
  const { isAuthenticated, isHydrating, isOwner } = useAuthSession();

  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isHydrating) return;

    if (!isAuthenticated) {
      navigate('/', { replace: true });
      return;
    }

    if (!isOwner) {
      toast.error('Você não tem permissão para acessar as unidades.');
      navigate('/products', { replace: true });
    }
  }, [isAuthenticated, isHydrating, isOwner, navigate]);

  useEffect(() => {
    if (isHydrating || !isOwner || !isEditing) return;

    let active = true;
    setLoading(true);
    getUnit(id)
      .then((data) => {
        if (!active) return;
        setForm({
          ...defaultForm,
          ...data,
        });
      })
      .catch((err) => {
        const message = err?.message || 'Falha ao carregar a unidade.';
        setError(message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [id, isEditing, isHydrating, isOwner]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);

    const payload = {
      ...form,
      unidade_id: isEditing ? Number(id) : undefined,
    };

    try {
      await saveUnit(payload);
      toast.success('Unidade salva com sucesso.');
      navigate('/unidades');
    } catch (err) {
      const message = err?.message || 'Erro ao salvar a unidade.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  if (isHydrating) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isOwner) {
    return null;
  }

  return (
    <Box sx={{ maxWidth: '780px', mx: 'auto', p: { xs: 2, md: 4 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} color="inherit">
            Voltar
          </Button>
          <Typography variant="h5" fontWeight="bold">
            {isEditing ? 'Editar unidade' : 'Nova unidade'}
          </Typography>
        </Stack>
        <SettingsMenuButton buttonProps={{ color: 'default' }} />
      </Stack>

      <Paper component="form" onSubmit={handleSubmit} sx={{ p: { xs: 2, md: 3 } }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Stack alignItems="center" spacing={2} py={4}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">
              Carregando unidade...
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={2}>
            <TextField
              label="Nome da unidade"
              name="nome"
              value={form.nome}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              label="Endereço"
              name="endereco"
              value={form.endereco || ''}
              onChange={handleChange}
              fullWidth
            />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Cidade"
                name="cidade"
                value={form.cidade || ''}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                label="Estado"
                name="estado"
                value={form.estado || ''}
                onChange={handleChange}
                fullWidth
              />
            </Stack>
            <TextField
              label="CEP"
              name="cep"
              value={form.cep || ''}
              onChange={handleChange}
              fullWidth
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={Boolean(form.ativo)}
                  onChange={handleChange}
                  name="ativo"
                  color="primary"
                />
              )}
              label="Unidade ativa"
            />
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button
                type="submit"
                variant="contained"
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                disabled={saving}
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
            </Stack>
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default UnitForm;
